import React, { useEffect, useMemo, useState } from "react";
import { useSpecialistProfile } from "../state/SpecialistProfileContext";

type LanguageRow = {
  LanguageID: number;
  Name: string | null;
};

type VisitTypeRow = {
  VisitTypeID: number;
  Name: string | null;
};

type CoverageTypeRow = {
  CoverageTypeID: number;
  Name: string | null;
};

const TabAccessCoverage: React.FC = () => {
  const { formState, updateAccessField, accessErrors } = useSpecialistProfile();

  const { languageIds, visitTypeIds, coverageTypeIds } = formState.access;

  const [languages, setLanguages] = useState<LanguageRow[]>([]);
  const [visitTypes, setVisitTypes] = useState<VisitTypeRow[]>([]);
  const [coverageTypes, setCoverageTypes] = useState<CoverageTypeRow[]>([]);

  const [loadingLanguages, setLoadingLanguages] = useState(false);
  const [loadingVisitTypes, setLoadingVisitTypes] = useState(false);
  const [loadingCoverageTypes, setLoadingCoverageTypes] = useState(false);

  const [errorLanguages, setErrorLanguages] = useState<string>("");
  const [errorVisitTypes, setErrorVisitTypes] = useState<string>("");
  const [errorCoverageTypes, setErrorCoverageTypes] = useState<string>("");

  const fetchJsonArray = async (url: string) => {
    const res = await fetch(url, { headers: { Accept: "application/json" } });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(text || `Request failed (status ${res.status})`);
    }
    const data = await res.json();
    if (!Array.isArray(data)) {
      throw new Error(`Unexpected response format from ${url}`);
    }
    return data;
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      // Languages
      setLoadingLanguages(true);
      setErrorLanguages("");
      try {
        const data = await fetchJsonArray("/api/languages");
        if (!cancelled) setLanguages(data);
      } catch (e: any) {
        if (!cancelled) {
          setLanguages([]);
          setErrorLanguages(e?.message || "Failed to load languages.");
        }
      } finally {
        if (!cancelled) setLoadingLanguages(false);
      }

      // Visit types
      setLoadingVisitTypes(true);
      setErrorVisitTypes("");
      try {
        const data = await fetchJsonArray("/api/visit-types");
        if (!cancelled) setVisitTypes(data);
      } catch (e: any) {
        if (!cancelled) {
          setVisitTypes([]);
          setErrorVisitTypes(e?.message || "Failed to load visit types.");
        }
      } finally {
        if (!cancelled) setLoadingVisitTypes(false);
      }

      // Coverage types
      setLoadingCoverageTypes(true);
      setErrorCoverageTypes("");
      try {
        const data = await fetchJsonArray("/api/coverage-types");
        if (!cancelled) setCoverageTypes(data);
      } catch (e: any) {
        if (!cancelled) {
          setCoverageTypes([]);
          setErrorCoverageTypes(e?.message || "Failed to load coverage types.");
        }
      } finally {
        if (!cancelled) setLoadingCoverageTypes(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const languageOptions = useMemo(() => {
    return languages
      .filter((l) => typeof l?.LanguageID === "number")
      .map((l) => ({
        id: String(l.LanguageID),
        name: l.Name && l.Name.trim() ? l.Name.trim() : `Language ${l.LanguageID}`,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [languages]);

  const visitTypeOptions = useMemo(() => {
    return visitTypes
      .filter((v) => typeof v?.VisitTypeID === "number")
      .map((v) => ({
        id: String(v.VisitTypeID),
        name: v.Name && v.Name.trim() ? v.Name.trim() : `Visit type ${v.VisitTypeID}`,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [visitTypes]);

  const coverageTypeOptions = useMemo(() => {
    return coverageTypes
      .filter((c) => typeof c?.CoverageTypeID === "number")
      .map((c) => ({
        id: String(c.CoverageTypeID),
        name:
          c.Name && c.Name.trim()
            ? c.Name.trim()
            : `Coverage type ${c.CoverageTypeID}`,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [coverageTypes]);

  const toggleMulti = (field: "languageIds" | "visitTypeIds" | "coverageTypeIds", id: string) => {
    const current = (formState.access as any)[field] as string[];
    const safeCurrent = Array.isArray(current) ? current : [];
    const exists = safeCurrent.includes(id);

    const updated = exists ? safeCurrent.filter((x) => x !== id) : [...safeCurrent, id];
    updateAccessField(field, updated);
  };

  const sectionBoxStyle: React.CSSProperties = {
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: 12,
    background: "#fafafa",
  };

  return (
    <div>
      <p style={{ marginBottom: 16 }}>
        Select what patients can expect regarding <strong>language</strong>,{" "}
        <strong>visit type</strong>, and <strong>coverage</strong>.
      </p>

      {/* Languages */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", marginBottom: 6 }}>
          Languages offered <span style={{ color: "red" }}>*</span>
        </label>

        <div style={sectionBoxStyle}>
          {loadingLanguages ? (
            <div style={{ fontSize: 13, opacity: 0.8 }}>Loading languages…</div>
          ) : errorLanguages ? (
            <div style={{ color: "red" }}>Could not load languages: {errorLanguages}</div>
          ) : languageOptions.length === 0 ? (
            <div style={{ fontSize: 13, opacity: 0.8 }}>No languages found in DB.</div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {languageOptions.map((opt) => {
                const checked = (languageIds || []).includes(opt.id);
                return (
                  <label
                    key={opt.id}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 10px",
                      borderRadius: 16,
                      border: checked ? "2px solid #0017a2" : "1px solid #ccc",
                      backgroundColor: checked ? "#eef1ff" : "#fff",
                      cursor: "pointer",
                      fontSize: 14,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleMulti("languageIds", opt.id)}
                    />
                    <span>{opt.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {accessErrors.languageIds && (
          <div style={{ color: "red", marginTop: 6 }}>{accessErrors.languageIds}</div>
        )}
      </div>

      {/* Visit types */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", marginBottom: 6 }}>
          Visit types supported <span style={{ color: "red" }}>*</span>
        </label>

        <div style={sectionBoxStyle}>
          {loadingVisitTypes ? (
            <div style={{ fontSize: 13, opacity: 0.8 }}>Loading visit types…</div>
          ) : errorVisitTypes ? (
            <div style={{ color: "red" }}>
              Could not load visit types: {errorVisitTypes}
            </div>
          ) : visitTypeOptions.length === 0 ? (
            <div style={{ fontSize: 13, opacity: 0.8 }}>No visit types found in DB.</div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {visitTypeOptions.map((opt) => {
                const checked = (visitTypeIds || []).includes(opt.id);
                return (
                  <label
                    key={opt.id}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 10px",
                      borderRadius: 16,
                      border: checked ? "2px solid #0017a2" : "1px solid #ccc",
                      backgroundColor: checked ? "#eef1ff" : "#fff",
                      cursor: "pointer",
                      fontSize: 14,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleMulti("visitTypeIds", opt.id)}
                    />
                    <span>{opt.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {accessErrors.visitTypeIds && (
          <div style={{ color: "red", marginTop: 6 }}>{accessErrors.visitTypeIds}</div>
        )}
      </div>

      {/* Coverage types */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "block", marginBottom: 6 }}>
          Coverage accepted <span style={{ color: "red" }}>*</span>
        </label>

        <div style={sectionBoxStyle}>
          {loadingCoverageTypes ? (
            <div style={{ fontSize: 13, opacity: 0.8 }}>Loading coverage types…</div>
          ) : errorCoverageTypes ? (
            <div style={{ color: "red" }}>
              Could not load coverage types: {errorCoverageTypes}
            </div>
          ) : coverageTypeOptions.length === 0 ? (
            <div style={{ fontSize: 13, opacity: 0.8 }}>No coverage types found in DB.</div>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {coverageTypeOptions.map((opt) => {
                const checked = (coverageTypeIds || []).includes(opt.id);
                return (
                  <label
                    key={opt.id}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                      padding: "6px 10px",
                      borderRadius: 16,
                      border: checked ? "2px solid #0017a2" : "1px solid #ccc",
                      backgroundColor: checked ? "#eef1ff" : "#fff",
                      cursor: "pointer",
                      fontSize: 14,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleMulti("coverageTypeIds", opt.id)}
                    />
                    <span>{opt.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {accessErrors.coverageTypeIds && (
          <div style={{ color: "red", marginTop: 6 }}>
            {accessErrors.coverageTypeIds}
          </div>
        )}
      </div>
    </div>
  );
};

export default TabAccessCoverage;
